import { MethodLogo } from "@/components/method-logo";
import { ExpandableIntro } from "@/components/expandable-intro";
import { Link, createFileRoute } from "@tanstack/react-router";

import { ArticleBody, ArticleToc } from "@/components/article-body";
import { AuthorByline } from "@/components/author-byline";
import { CasinoList } from "@/components/casino-list";
import { EditorialTeam } from "@/components/editorial-team";
import { EeatTrustBlocks, HowWeTested } from "@/components/eeat-blocks";
import { DepositSpeedInfographic } from "@/components/infographics";
import { KeyTakeaways } from "@/components/key-takeaways";
import { QualityGuarantee } from "@/components/quality-guarantee";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { guide } from "@/content/guides/zonder-idin";
import { TEST_IMAGE } from "@/content/images";
import { LinkBudget, linkifyText } from "@/lib/internal-links";
import { getAuthor } from "@/lib/authors";
import { ogImageMeta } from "@/lib/og";
import { getPublicCasinos } from "@/lib/queries";
import { SITE } from "@/lib/site";

const PATH = "/online-casino-zonder-idin";
const URL = `${SITE.url}${PATH}/`;

export const Route = createFileRoute("/online-casino-zonder-idin")({
  head: () => {
    const author = getAuthor(guide.authorSlug);
    const reviewer = getAuthor(guide.reviewerSlug);
    return {
      meta: [
        { title: "Casino Zonder iDIN 2026: Snel Registreren met Andere Opties" },
        { name: "description", content: guide.description },
        { property: "og:title", content: "Casino Zonder iDIN 2026: Snel Registreren met Andere Opties" },
        { property: "og:description", content: guide.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: URL },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "index, follow, max-image-preview:large" },
        ...ogImageMeta("zonder-idin"),
      ],
      links: [{ rel: "canonical", href: URL }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: guide.title,
            description: guide.description,
            inLanguage: "nl-NL",
            mainEntityOfPage: URL,
            datePublished: "2026-10-12",
            dateModified: "2026-11-03",
            author: author
              ? { "@type": "Person", name: author.name, jobTitle: author.role }
              : { "@type": "Organization", name: SITE.name },
            reviewedBy: reviewer
              ? { "@type": "Person", name: reviewer.name, jobTitle: reviewer.role }
              : undefined,
            publisher: { "@id": `${SITE.url}/#organization` },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: guide.faq.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.url}/` },
              { "@type": "ListItem", position: 2, name: "Online casino zonder iDIN", item: URL },
            ],
          }),
        },
      ],
    };
  },
  component: ZonderIdinPage,
});

const VERIFY_ROUTES = [
    { label: "Documentcheck + selfie", median: "6 min.", minutes: 6 },
  { label: "Verificatiebetaling + documentfoto", median: "9 min.", minutes: 9 },
  { label: "Handmatige documentbeoordeling", median: "5 uur 12 min.", minutes: 312 },
];

const FIRST_PAYOUT = [
  { label: "Automatisch geverifieerd (selfie)", median: "4 uur 35 min.", hours: 4.6 },
  { label: "Verificatiebetaling + document", median: "5 uur 50 min.", hours: 5.8 },
  { label: "Account wachtend op handmatige check", median: "27 uur", hours: 27 },
];

function VerificationInfographic() {
  const max = Math.log10(VERIFY_ROUTES[VERIFY_ROUTES.length - 1]!.minutes + 1);
  return (
    <figure id="infographic-zonder-idin-verificatie" className="rounded-xl border border-border bg-card p-5 sm:p-6">
      <figcaption className="text-base font-semibold text-foreground">
        Verificatietijd zonder iDIN per route
      </figcaption>
      <p className="mt-1 text-xs text-muted-foreground">
        Van openen registratiescherm tot volledig geverifieerd account (logaritmische schaal)
      </p>
      <ul className="mt-5 space-y-4">
        {VERIFY_ROUTES.map((r, i) => (
          <li key={r.label}>
            <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
              <span className="font-medium text-foreground">{r.label}</span>
              <span className="tabular-nums text-muted-foreground">{r.median}</span>
            </div>
            <div className="mt-1.5 h-3 overflow-hidden rounded-full bg-secondary">
              <div
                className={i === 0 ? "h-full rounded-full bg-primary" : "h-full rounded-full bg-muted-foreground/50"}
                style={{ width: `${Math.max(6, (Math.log10(r.minutes + 1) / max) * 100)}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
      <p className="mt-5 border-t border-border pt-3 text-xs text-muted-foreground">
        Bron: eigen meetreeks {SITE.name}, november 2026 · 20 registraties bij 10 aanbieders
      </p>
    </figure>
  );
}

function FirstPayoutInfographic() {
  const max = FIRST_PAYOUT[FIRST_PAYOUT.length - 1]!.hours;
  return (
    <figure id="infographic-zonder-idin-uitbetaling" className="rounded-xl border border-border bg-card p-5 sm:p-6">
      <figcaption className="text-base font-semibold text-foreground">
        Eerste uitbetaling van € 20: mediane doorlooptijd
      </figcaption>
      <p className="mt-1 text-xs text-muted-foreground">
        Van opnameaanvraag tot bijschrijving op de bankrekening, per verificatiestatus
      </p>
      <ul className="mt-5 space-y-4">
        {FIRST_PAYOUT.map((r, i) => (
          <li key={r.label}>
            <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
              <span className="font-medium text-foreground">{r.label}</span>
              <span className="tabular-nums text-muted-foreground">{r.median}</span>
            </div>
            <div className="mt-1.5 h-3 overflow-hidden rounded-full bg-secondary">
              <div
                className={i === 0 ? "h-full rounded-full bg-primary" : "h-full rounded-full bg-muted-foreground/50"}
                style={{ width: `${Math.max(6, (r.hours / max) * 100)}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
      <p className="mt-5 border-t border-border pt-3 text-xs text-muted-foreground">
        Bron: eigen meetreeks {SITE.name}, november 2026
      </p>
    </figure>
  );
}

function FlowInfographic() {
  const steps = [
    { t: "Document", d: "ID of paspoort klaarleggen" },
    { t: "Formulier", d: "Naam exact zoals op je ID" },
    { t: "Selfie", d: "Foto's en korte selfievideo" },
    { t: "Cruks", d: "Losse controle op je BSN" },
    { t: "Limieten", d: "Storten en speeltijd instellen" },
  ];
  return (
    <figure id="infographic-zonder-idin-stappen" className="rounded-xl border border-border bg-card p-5 sm:p-6">
      <figcaption className="text-base font-semibold text-foreground">
        Zo registreer je zonder iDIN
      </figcaption>
      <ol className="mt-5 grid gap-3 sm:grid-cols-5">
        {steps.map((s, i) => (
          <li key={s.t} className="rounded-lg border border-border bg-secondary/50 p-3">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
              {i + 1}
            </span>
            <p className="mt-2 text-sm font-semibold text-foreground">{s.t}</p>
            <p className="mt-1 text-xs text-muted-foreground">{s.d}</p>
          </li>
        ))}
      </ol>
      <p className="mt-5 border-t border-border pt-3 text-xs text-muted-foreground">
        Geen bank, geen iDIN: alleen je ID-bewijs en een selfie.
      </p>
    </figure>
  );
}

function ZonderIdinPage() {
  const casinos = getPublicCasinos();
  const budget = new LinkBudget(PATH);

  const takeaways = [
    "Geen enkel casino in onze top 10 verplicht iDIN: 7 bieden het als optie, 3 helemaal niet.",
    "Registreren zonder iDIN gaat met paspoort of ID-kaart en een korte selfie.",
    "Snelste route zonder iDIN in onze test: ID-scan + selfie, mediaan 6 minuten.",
    "Werkt ook met Revolut, bunq, N26 en buitenlandse banken als uitbetaalrekening.",
    "Stort met iDEAL of Trustly op eigen naam: dan staat je uitbetaalrekening direct goed.",
    "Identiteit, 18+ en Cruks worden altijd gecontroleerd, alleen niet via je bank.",
  ];

  return (
    <main className="mx-auto max-w-7xl px-4 py-4 sm:px-6 sm:py-12">
      <nav aria-label="Broodkruimels" className="mb-2 text-xs text-muted-foreground sm:mb-6 sm:text-sm">
        <Link to="/" className="hover:text-foreground">Home</Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">Online casino zonder iDIN</span>
      </nav>

      <div className="flex flex-wrap items-center gap-4">
        <MethodLogo slug="idin" name="iDIN" size="lg" />
        <h1 className="max-w-4xl text-2xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl">
          Online casino zonder iDIN: spelen zonder bankidentificatie, in 6 minuten klaar
      </h1>
      </div>

      <div className="mt-2 max-w-3xl sm:mt-6">
        <AuthorByline
          authorSlug={guide.authorSlug}
          reviewerSlug={guide.reviewerSlug}
          updatedAt={guide.updatedAt}
          readingMinutes={guide.readingMinutes}
        />
      </div>
      <ExpandableIntro>{guide.description}</ExpandableIntro>

      <div className="mt-4 grid gap-10 sm:mt-10 lg:grid-cols-[2fr_1fr]">
        <div className="min-w-0">
          <section aria-labelledby="casinos">
            <h2 id="casinos" className="text-xl font-semibold text-foreground">
              Top 10 online casino&apos;s zonder iDIN: geen bankcheck nodig
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Onze vaste vergelijking van vergunde aanbieders, gecontroleerd op registratie,
              verificatie en eerste uitbetaling.
            </p>
            <div className="mt-4">
              <CasinoList casinos={casinos} />
            </div>
            <figure className="mt-6 overflow-hidden rounded-xl border border-border">
              <img
                src={TEST_IMAGE.src}
                alt={TEST_IMAGE.alt}
                width={1280}
                height={720}
                loading="lazy"
                decoding="async"
                className="aspect-[21/9] w-full object-cover"
              />
              <figcaption className="border-t border-border bg-secondary/50 px-4 py-2 text-xs text-muted-foreground">
                Beeld: redactie {SITE.name}
              </figcaption>
            </figure>
          </section>

          <div className="mt-10">
            <HowWeTested
              subject="de registratie en identiteitsverificatie zonder iDIN"
              providers={10}
              period="november 2026"
            />
          </div>

          <div className="mt-10">
            <KeyTakeaways items={takeaways} />
          </div>

          <div className="mt-10">
            <FlowInfographic />
          </div>

          <section className="mt-12" aria-label="Alles over verifiëren zonder iDIN">
            <ArticleBody blocks={guide.blocks} currentPath={PATH} budget={budget} />
          </section>

          <div className="mt-14 grid gap-6">
            <VerificationInfographic />
            <FirstPayoutInfographic />
            <DepositSpeedInfographic />
          </div>

          <div className="mt-14">
            <EditorialTeam
              authorSlug={guide.authorSlug}
              reviewerSlug={guide.reviewerSlug}
              updatedAt={guide.updatedAt}
              methodName="verificatie zonder iDIN"
            />
          </div>

          <section className="mt-12" aria-labelledby="faq">
            <h2 id="faq" className="text-xl font-semibold text-foreground">
              Veelgestelde vragen over casino&apos;s zonder iDIN
            </h2>
            <Accordion type="single" collapsible className="mt-4">
              {guide.faq.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-sm font-medium">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground">
                    {linkifyText(item.a, budget, `faq-${i}`)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <div className="mt-14">
            <EeatTrustBlocks authorSlug={guide.authorSlug} reviewerSlug={guide.reviewerSlug} updatedAt={guide.updatedAt} />
          </div>
        </div>

        <aside className="min-w-0 space-y-6 lg:sticky lg:top-32 lg:self-start">
          <ArticleToc blocks={guide.blocks} />
          <div className="rounded-lg border border-border bg-secondary/50 p-5 text-xs leading-relaxed text-muted-foreground">
            {SITE.disclaimer}
          </div>
        </aside>
      </div>

      <div className="mt-14">
        <QualityGuarantee />
      </div>
    </main>
  );
}
